import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { CalendarClock, ListChecks, MessageCircleQuestion, Plus, SquareKanban } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { TaskSummary } from "@/api/generated/TaskSummary"
import { useTasks } from "@/api/queries/tasks"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useDebounced } from "@/features/studio/use-debounced"
import { checklistProgress, isOverdue } from "@/features/tasks/logic"
import { TaskMenuItems } from "@/features/tasks/task-actions"
import { AssigneeLabel, PriorityLabel, TaskNumber, TaskStatusBadge } from "@/features/tasks/task-badges"
import { TaskSheet } from "@/features/tasks/task-detail"
import {
  hasTaskFilters,
  LiveIndicator,
  parseTaskFilterSearch,
  TaskFilterControls,
  type TaskFilterSearch,
  toTaskFilters,
} from "@/features/tasks/task-filters"
import { TaskCreateSheet } from "@/features/tasks/task-form"
import { TaskProgress } from "@/features/tasks/task-sections"
import { useTaskBoardEvents } from "@/features/tasks/use-task-events"
import { formatDateTime } from "@/lib/format"
import { cn } from "@/lib/utils"

interface TasksSearch extends TaskFilterSearch {
  page?: number
  size?: number
  /** id of the task open in the sheet */
  task?: number
}

const num = (v: unknown) => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined
}

export const Route = createFileRoute("/_app/tasks/")({
  staticData: { titleKey: "nav.items.allTasks" },
  validateSearch: (s: Record<string, unknown>): TasksSearch => ({
    ...parseTaskFilterSearch(s),
    page: num(s.page),
    size: num(s.size),
    task: num(s.task),
  }),
  component: TasksPage,
})

function TasksPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const live = useTaskBoardEvents()
  const [creating, setCreating] = useState(false)
  const [query, setQuery] = useState(search.q ?? "")
  const debounced = useDebounced(query, 300)
  const page = search.page ?? 1
  const size = search.size ?? 50

  const setSearch = (patch: Partial<TasksSearch>, resetPage = true) =>
    void navigate({
      search: (prev) => ({ ...prev, ...patch, ...(resetPage ? { page: undefined } : {}) }),
      replace: true,
    })

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the debounced value
  useEffect(() => {
    if ((debounced || undefined) !== search.q) setSearch({ q: debounced || undefined })
  }, [debounced])

  const filters = {
    ...toTaskFilters(search),
    order_by: search.sort ?? "updated_at",
    limit: size,
    offset: (page - 1) * size,
  }
  // SSE is the primary update channel; poll only while it is down and agents are working
  const tasks = useTasks(filters, {
    refetchInterval: live !== "open" ? 10_000 : false,
  })
  const waiting = useTasks({ status: "waiting", limit: 1 })
  const review = useTasks({ status: "review", limit: 1 })
  const waitingCount = waiting.data?.total ?? 0
  const reviewCount = review.data?.total ?? 0
  const filtered = hasTaskFilters(search)

  const columns = useMemo(
    () =>
      createColumns<TaskSummary>((c) => [
        c.accessor("title", {
          header: t("tasks.hub.columns.task"),
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }) => {
            const task = row.original
            const check = checklistProgress(task.checklist)
            return (
              <div className="flex max-w-[34rem] min-w-56 items-start gap-2">
                <TaskNumber task={task} className="mt-0.5 w-9 shrink-0" />
                <div className="min-w-0 space-y-0.5">
                  <div className="truncate font-medium">{task.title}</div>
                  <div className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                    <span className="truncate">{task.project_name ?? t("tasks.hub.filters.standalone")}</span>
                    {check.total > 0 && (
                      <span className="tabular-nums">
                        {t("tasks.task.checklist.progress", { done: check.done, total: check.total })}
                      </span>
                    )}
                    {task.labels.slice(0, 3).map((l) => (
                      <Badge key={l} variant="outline" className="h-4 px-1 text-[0.65rem] font-normal">
                        {l}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )
          },
        }),
        c.accessor("display_status", {
          header: t("tasks.hub.columns.status"),
          enableSorting: false,
          cell: ({ row }) => <TaskStatusBadge task={row.original} />,
        }),
        c.accessor("priority", {
          header: t("tasks.hub.columns.priority"),
          enableSorting: false,
          cell: ({ getValue }) => <PriorityLabel priority={getValue()} className="text-[0.8125rem]" />,
        }),
        c.accessor("assignee_name", {
          header: t("tasks.hub.columns.assignee"),
          enableSorting: false,
          cell: ({ row }) => <AssigneeLabel task={row.original} className="max-w-44 text-[0.8125rem]" />,
        }),
        c.accessor("progress_percent", {
          header: t("tasks.hub.columns.progress"),
          enableSorting: false,
          cell: ({ getValue }) => {
            const v = getValue()
            return v == null ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <div className="flex w-24 items-center gap-2">
                <TaskProgress value={v} />
                <span className="text-muted-foreground text-xs tabular-nums">{v}%</span>
              </div>
            )
          },
        }),
        c.accessor("due_at", {
          header: t("tasks.hub.columns.due"),
          enableSorting: false,
          cell: ({ row }) =>
            row.original.due_at ? (
              <span
                className={cn(
                  "inline-flex items-center gap-1 text-[0.8125rem] whitespace-nowrap",
                  isOverdue(row.original) ? "text-destructive" : "text-muted-foreground",
                )}
              >
                <CalendarClock className="size-3.5" aria-hidden />
                {formatDateTime(row.original.due_at, "PP")}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
        }),
        c.accessor("updated_at", {
          header: t("tasks.hub.columns.updated"),
          enableSorting: false,
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  return (
    <PageContainer width="full" className="max-w-[100rem]">
      <PageHeader
        title={t("tasks.hub.title")}
        description={t("tasks.hub.description")}
        icon={ListChecks}
        actions={
          <>
            <Button variant="outline" nativeButton={false} render={<Link to="/tasks/board" search={{}} />}>
              <SquareKanban />
              {t("tasks.hub.openBoard")}
            </Button>
            <Button onClick={() => setCreating(true)} data-testid="new-task">
              <Plus />
              {t("tasks.hub.new")}
            </Button>
          </>
        }
      />
      {(waitingCount > 0 || reviewCount > 0) && (
        <div
          className="border-warning/30 bg-warning/5 mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border px-4 py-2.5 text-sm"
          role="status"
        >
          <MessageCircleQuestion className="text-warning size-4 shrink-0" aria-hidden />
          <span className="font-medium">
            {t("tasks.hub.attention", { count: waitingCount + reviewCount })}
          </span>
          <div className="flex flex-wrap gap-2">
            {waitingCount > 0 && (
              <Button size="xs" variant="outline" onClick={() => setSearch({ status: "waiting" })}>
                {t("tasks.hub.attentionWaiting", { count: waitingCount })}
              </Button>
            )}
            {reviewCount > 0 && (
              <Button size="xs" variant="outline" onClick={() => setSearch({ status: "review" })}>
                {t("tasks.hub.attentionReview", { count: reviewCount })}
              </Button>
            )}
          </div>
        </div>
      )}
      <DataTable
        label={t("tasks.hub.title")}
        data={tasks.data?.items}
        columns={columns}
        getRowId={(r) => String(r.id)}
        loading={tasks.isPending}
        error={tasks.error}
        onRetry={() => void tasks.refetch()}
        searchable
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder={t("tasks.hub.search")}
        columnToggle
        persistKey="tasks-list"
        initialColumnVisibility={{ progress_percent: false }}
        serverPagination={{
          pageIndex: page - 1,
          pageSize: size,
          rowCount: tasks.data?.total ?? 0,
          onChange: (p) =>
            setSearch(
              {
                page: p.pageIndex + 1 > 1 ? p.pageIndex + 1 : undefined,
                size: p.pageSize === 50 ? undefined : p.pageSize,
              },
              false,
            ),
        }}
        onRowClick={(r) => setSearch({ task: r.id }, false)}
        isRowActive={(r) => r.id === search.task}
        rowActions={(r) => (
          <TaskMenuItems
            task={r}
            onOpen={() => setSearch({ task: r.id }, false)}
            onOpenPage={() => void navigate({ to: "/tasks/$taskId", params: { taskId: String(r.id) } })}
          />
        )}
        toolbar={
          <>
            <TaskFilterControls value={search} onChange={(patch) => setSearch(patch)} />
            {filtered && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQuery("")
                  setSearch({
                    q: undefined,
                    status: undefined,
                    project: undefined,
                    assignee: undefined,
                    priority: undefined,
                  })
                }}
              >
                {t("tasks.hub.filters.clear")}
              </Button>
            )}
          </>
        }
        toolbarEnd={<LiveIndicator status={live} />}
        emptyState={
          filtered ? (
            <EmptyState
              variant="plain"
              icon={ListChecks}
              title={t("tasks.hub.emptyFiltered.title")}
              description={t("tasks.hub.emptyFiltered.description")}
            />
          ) : (
            <EmptyState
              variant="plain"
              icon={ListChecks}
              title={t("tasks.hub.empty.title")}
              description={t("tasks.hub.empty.description")}
              action={
                <Button onClick={() => setCreating(true)}>
                  <Plus />
                  {t("tasks.hub.new")}
                </Button>
              }
            />
          )
        }
      />
      <TaskSheet
        taskId={search.task}
        onClose={() => setSearch({ task: undefined }, false)}
        onOpenTask={(id) => setSearch({ task: id }, false)}
      />
      <TaskCreateSheet
        open={creating}
        onOpenChange={setCreating}
        defaults={{ project_id: typeof search.project === "number" ? search.project : null }}
        onCreated={(task) => setSearch({ task: task.id }, false)}
      />
    </PageContainer>
  )
}
